import { SignOutIcon, UserIcon } from "@/components/icons";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "cn";

import type { Me } from "../lib/auth.ts";
import { analytics } from "../lib/telemetry/analytics.ts";

import { useWorkspace } from "./AppShell.tsx";
import { displayNameOf, initialsOf } from "./identity.ts";
import { useSession } from "./session.tsx";

/**
 * Quién está dentro, arriba a la derecha, y la salida.
 *
 * Lo monta `TopBar` y no cada armazón, como el contador de consumo: es un
 * mueble fijo de los dos modos, y dos copias se separan al primer retoque.
 *
 * El menú enseña lo que trae `/me` --nombre, correo, usuario y roles-- y lo
 * que no trae no sale: ni un "—" ni un campo inventado. Los permisos no se
 * enseñan: son nombres técnicos (`embajadores:usage.read`) y su efecto ya se
 * ve en qué botones aparecen.
 */
export function UserMenu() {
  const { me, signOut } = useSession();
  const { chat } = useWorkspace();

  // Sin nadie dentro no hay a quién nombrar. Con la sesión caducada `me`
  // sigue siendo quien era hasta que el navegador vuelve al SSO.
  if (me === null) return null;

  const name = displayNameOf(me);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 gap-2 rounded-full px-1 xl:pr-3"
          aria-label={name === "" ? "Tu cuenta" : `Tu cuenta: ${name}`}
        >
          <UserAvatar me={me} />
          {/* El nombre solo cuando sobra sitio: en poco ancho la cabecera
              ya lleva el conmutador y lo contextual, y el avatar basta. */}
          <span className="hidden max-w-40 truncate text-sm font-medium xl:inline">
            {name}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        // En un móvil estrecho el menú no puede salirse por el borde.
        className="w-72 max-w-[calc(100vw-1rem)]"
      >
        <DropdownMenuLabel className="flex items-center gap-3 py-2 font-normal">
          <UserAvatar me={me} className="size-10" />
          <div className="grid min-w-0 gap-0.5">
            {me.fullName !== "" && (
              <span className="truncate font-medium" title={me.fullName}>
                {me.fullName}
              </span>
            )}
            {/* El correo entero, partido si hace falta: es lo que dice con
                qué cuenta se entró, y cortado con "…" no se distingue de otra. */}
            {me.email !== "" && (
              <span className="text-xs break-all text-muted-foreground">
                {me.email}
              </span>
            )}
            {me.username !== "" && me.username !== me.email && (
              <span
                className="truncate font-mono text-xs text-muted-foreground"
                title={me.username}
              >
                {me.username}
              </span>
            )}
          </div>
        </DropdownMenuLabel>

        {me.roles.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="grid gap-1.5 px-2 py-1.5">
              <span className="text-xs text-muted-foreground">
                {me.roles.length === 1 ? "Rol" : "Roles"}
              </span>
              <ul className="flex flex-wrap gap-1">
                {me.roles.map((role) => (
                  <li key={role}>
                    <Badge variant="secondary">{role}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            // Un turno a medias no puede seguir escribiendo en una
            // conversación de quien ya se fue.
            analytics.auth.logout({ origin: "user_menu" });
            void signOut(chat.stop);
          }}
        >
          <SignOutIcon />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserAvatar({
  me,
  className,
}: {
  readonly me: Me;
  readonly className?: string;
}) {
  const initials = initialsOf(me);
  return (
    // `/me` no trae foto: el avatar son las iniciales, o un icono si ni eso.
    <Avatar className={cn("size-7", className)} aria-hidden>
      <AvatarFallback className="bg-primary text-xs font-medium text-primary-foreground">
        {initials === "" ? <UserIcon /> : initials}
      </AvatarFallback>
    </Avatar>
  );
}
